import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ChatFlowService, FlowTopic } from '../services/chat.flow.service';

@Component({
    selector: 'flow-topic-list',
    template: require('./topic-list.component.html')
})
export class TopicListComponent implements OnInit {

    @Input() project: any;
    @Input() selectedTopicId: string | null = null;

    @Output() topicSelected = new EventEmitter<string | null>();
    @Output() newChat = new EventEmitter<void>();

    topics: FlowTopic[] = [];
    loading: boolean = false;
    error: string | null = null;

    renamingTopicId: string | null = null;
    renameValue: string = '';

    deletingTopicId: string | null = null;

    constructor(private chatFlowService: ChatFlowService) {}

    ngOnInit() {
        this.fetchTopics();
    }

    fetchTopics() {
        this.loading = true;
        this.error = null;
        this.chatFlowService.listTopics(this.project.unixName)
            .then(topics => {
                this.topics = topics;
                this.loading = false;
            })
            .catch(() => {
                this.error = 'Failed to load conversations.';
                this.loading = false;
            });
    }

    onNewChat() {
        this.newChat.emit();
    }

    selectTopic(topicId: string) {
        if (this.renamingTopicId !== null || this.deletingTopicId !== null) {
            return;
        }
        this.topicSelected.emit(topicId);
    }

    // --- Rename ---

    startRename(topic: FlowTopic, event: Event) {
        event.stopPropagation();
        this.deletingTopicId = null;
        this.renamingTopicId = topic.id;
        this.renameValue = topic.title;
    }

    saveRename(topic: FlowTopic) {
        const newTitle = this.renameValue.trim();
        if (!newTitle || newTitle === topic.title) {
            this.cancelRename();
            return;
        }
        this.chatFlowService.updateTopic(this.project.unixName, topic.id, { title: newTitle })
            .then(updated => {
                topic.title = updated.title;
                this.renamingTopicId = null;
            })
            .catch(() => {
                this.cancelRename();
            });
    }

    cancelRename() {
        this.renamingTopicId = null;
        this.renameValue = '';
    }

    onRenameKeydown(event: KeyboardEvent, topic: FlowTopic) {
        if (event.key === 'Enter') {
            this.saveRename(topic);
        } else if (event.key === 'Escape') {
            this.cancelRename();
        }
    }

    // --- Delete ---

    promptDelete(topic: FlowTopic, event: Event) {
        event.stopPropagation();
        this.renamingTopicId = null;
        this.deletingTopicId = topic.id;
    }

    confirmDelete(topic: FlowTopic) {
        this.chatFlowService.deleteTopic(this.project.unixName, topic.id)
            .then(() => {
                this.topics = this.topics.filter(t => t.id !== topic.id);
                if (this.selectedTopicId === topic.id) {
                    this.topicSelected.emit(null);
                }
                this.deletingTopicId = null;
            })
            .catch(() => {
                this.deletingTopicId = null;
            });
    }

    cancelDelete() {
        this.deletingTopicId = null;
    }

    // --- Pin / Share toggles ---

    togglePin(topic: FlowTopic, event: Event) {
        event.stopPropagation();
        const is_pinned = !topic.is_pinned;
        this.chatFlowService.updateTopic(this.project.unixName, topic.id, { is_pinned })
            .then(updated => {
                topic.is_pinned = updated.is_pinned;
            });
    }

    toggleShared(topic: FlowTopic, event: Event) {
        event.stopPropagation();
        const is_shared = !topic.is_shared;
        this.chatFlowService.updateTopic(this.project.unixName, topic.id, { is_shared })
            .then(updated => {
                topic.is_shared = updated.is_shared;
            });
    }

    relativeDate(dateStr: string): string {
        const diffMs = Date.now() - new Date(dateStr).getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffMs < 1000 * 60 * 60 * 24) { return 'today'; }
        if (diffDays < 7) { return diffDays + 'd'; }
        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks < 5) { return diffWeeks + 'w'; }
        return Math.floor(diffDays / 30) + 'mo';
    }

    // --- Called by ChatComponent after creating a new topic ---

    addTopic(topic: FlowTopic) {
        this.topics = [topic, ...this.topics];
    }

}
